/**
 * Ordering's own configuration plane, as this surface talks to it (ORDR-119).
 *
 * RULING B9: `nodes/<uuid>/config/<DOMAIN>/` in paio-ordering, NEVER
 * paio-config's `config/values/` or `definitions/`. Every tunable ordering
 * reads lives here (`TreeConfigAuthority` over `config_policy`); paio-config
 * holds no `app: ordering` definition at all.
 *
 * - `read`: the existing GET (#9). Its `value` is the RESOLVED value, which
 *   may be an ancestor's: resolution is WHOLE-DOMAIN, so the nearest node that
 *   holds a value supplies all of it.
 * - `explain`: the existing walk; only each step's `note` is kept, because the
 *   note is already the server's plain sentence.
 * - `write`: w2c-ord-config's PUT (seam C1, NOT ON MAIN when this was written).
 *   The body is `{"payload": <whole object>}` and REPLACES this node's own
 *   value. The reply carries the same reading as the GET.
 *
 * A RESULT, NEVER A THROW, like `ApiClient` beneath it. A refusal comes back
 * with its RFC 9457 `extensions`, which is what the UI branches on; `detail`
 * is carried for display only.
 */

import type { ApiClient } from "../../api/client.js";
import type { ConfigDomain } from "./domains.js";

export type ConfigValue = Readonly<Record<string, unknown>>;

export interface ConfigReading {
  readonly value: ConfigValue | null;
  /** `null`: nothing in the ancestry sets the domain; built-in defaults apply. */
  readonly sourceNodeId: string | null;
  readonly sourceName: string | null;
  /** An OPEN enum: "LOCKED", "DELEGATED", or something newer. */
  readonly mode: string;
  /** The tree's answer only (an ancestor's LOCK). It knows no capability. */
  readonly writable: boolean;
  readonly resolvedAt: string;
  /** The server's own plain sentence, rendered verbatim. */
  readonly explanation: string;
}

export interface Failure {
  readonly ok: false;
  /** `null` is a network failure. */
  readonly status: number | null;
  readonly correlationId: string;
}

export type ReadOutcome = { readonly ok: true; readonly reading: ConfigReading } | Failure;
export type ExplainOutcome = { readonly ok: true; readonly notes: readonly string[] } | Failure;
export type WriteOutcome =
  | { readonly ok: true; readonly reading: ConfigReading }
  | (Failure & {
      readonly detail: string | null;
      readonly extensions: Readonly<Record<string, unknown>> | null;
    });

export interface ConfigApi {
  read(nodeId: string, domain: ConfigDomain): Promise<ReadOutcome>;
  explain(nodeId: string, domain: ConfigDomain): Promise<ExplainOutcome>;
  write(nodeId: string, domain: ConfigDomain, payload: ConfigValue): Promise<WriteOutcome>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function dataOf(body: unknown): Record<string, unknown> | null {
  return isRecord(body) && isRecord(body.data) ? body.data : null;
}

/** The #9 shape, or `null` when the body is not one: never a guessed default. */
function toReading(data: Record<string, unknown> | null): ConfigReading | null {
  if (data === null || typeof data.writable !== "boolean" || typeof data.explanation !== "string") {
    return null;
  }
  return {
    value: isRecord(data.value) ? data.value : null,
    sourceNodeId: typeof data.source_node_id === "string" ? data.source_node_id : null,
    sourceName: typeof data.source_name === "string" ? data.source_name : null,
    mode: typeof data.mode === "string" ? data.mode : "",
    writable: data.writable,
    resolvedAt: typeof data.resolved_at === "string" ? data.resolved_at : "",
    explanation: data.explanation,
  };
}

function path(nodeId: string, domain: ConfigDomain): string {
  return `ordering/nodes/${encodeURIComponent(nodeId)}/config/${domain}/`;
}

export function createConfigApi(client: ApiClient): ConfigApi {
  return {
    async read(nodeId, domain) {
      const response = await client.request(path(nodeId, domain));
      const reading = response.status === 200 ? toReading(dataOf(response.body)) : null;
      if (reading === null) {
        return { ok: false, status: response.status, correlationId: response.correlationId };
      }
      return { ok: true, reading };
    },

    async explain(nodeId, domain) {
      const response = await client.request(`${path(nodeId, domain)}explain/`);
      const steps = dataOf(response.body)?.steps;
      if (response.status !== 200 || !Array.isArray(steps)) {
        return { ok: false, status: response.status, correlationId: response.correlationId };
      }
      const notes = steps.flatMap((step) =>
        isRecord(step) && typeof step.note === "string" ? [step.note] : [],
      );
      return { ok: true, notes };
    },

    async write(nodeId, domain, payload) {
      const response = await client.request(path(nodeId, domain), {
        method: "PUT",
        json: { payload },
      });
      const ok = response.status === 200 || response.status === 201;
      const reading = ok ? toReading(dataOf(response.body)) : null;
      if (reading !== null) {
        return { ok: true, reading };
      }
      const body = isRecord(response.body) ? response.body : {};
      // RFC 9457 allows extension members nested or at the top level; seam C1
      // names them `extensions`, and a top-level `check` is accepted as well.
      const extensions = isRecord(body.extensions)
        ? body.extensions
        : typeof body.check === "string"
          ? body
          : null;
      return {
        ok: false,
        status: response.status,
        correlationId: response.correlationId,
        detail: typeof body.detail === "string" ? body.detail : null,
        extensions,
      };
    },
  };
}
